import { ReactElement, useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Loading, Tab, Tabs } from "@wfp/ui"
import { useAppSelector } from "hooks/hooksStore"
import { selectUser } from "features/common/commonSlice"
import { useGetXlsFormSchemasByIdQuery, useGetXlsFormFieldsWithGroupsQuery } from "services/household/xlsFormSchemas"
import ErrorState from "components/Shared/EmptyState/ErrorState"
import XlsFormDetails from "components/Household/XslForm/XlsFormDetails"
import XlsFormData from "components/Household/XslForm/XlsFormData"
import XslMappingProvider from "providers/XslMappingContext"
import XslFormMapping from "components/Household/XslForm/XslFormMapping"
import XlsFormChoice from "components/Household/XslForm/XlsFormChoice"
import { checkAccess } from "utils/helpers"
import { HH_MODULE } from "constants/constants"
import XlsFormList from "components/Household/XslForm/XlsFormList"
import XlsChoiceList from "components/Household/XslForm/XlsChoiceList"
import XlsSurveyLinked from "components/Household/XslForm/XlsSurveyLinked"
import "react-virtualized/styles.css"

const XlsFormPageHH = (): ReactElement => {
  const navigate = useNavigate()
  const { search, hash } = useLocation()
  const { xlsFormId } = useParams<{ xlsFormId: string }>()
  const mode = new URLSearchParams(search)?.get("mode") ?? "view"
  const [isValid, setIsValid] = useState<boolean>(false)

  const user = useAppSelector(selectUser)
  const [permissions, setPermissions] = useState<boolean>(false)

  const {
    data: xlsForm,
    isLoading: isLoadingXlsForm,
    error: errorXlsForm,
  } = useGetXlsFormSchemasByIdQuery(Number(xlsFormId), { skip: !xlsFormId })
  const {
    data: formFields,
    isLoading: isLoadingCOFields,
    error: errorCOFields,
  } = useGetXlsFormFieldsWithGroupsQuery({ xlsFormId: Number(xlsFormId) })

  const selectTab = (): number => {
    switch (hash) {
      case "#details":
        return 0
      case "#list":
        return 1
      case "#choice-list":
        return 2
      case "#mapping":
        return 3
      case "#choices":
        return 4
      case "#export":
        if (!isValid) return 1
        if (permissions) return 5
        return 3
      case "#linked-surveys":
        return permissions ? 6 : 4
      default:
        return 0
    }
  }

  const changeMode = (): void => {
    if (mode === "edit") {
      navigate(`/household/xlsFormSchema/${xlsFormId}#details`)
    } else {
      navigate(`/household/xlsFormSchema/${xlsFormId}?mode=edit#details`)
    }
  }

  useEffect(() => {
    if (xlsForm) {
      const check = checkAccess(user?.roles, [HH_MODULE], "xlsForms", "edit")
      setPermissions(check)
      setIsValid(xlsForm.isValid)
      !check && mode === "edit" && changeMode()
    }
  }, [xlsForm, user])

  if (isLoadingXlsForm || isLoadingCOFields) return <Loading small />
  if (errorXlsForm || errorCOFields) return <ErrorState />

  return (
    <XslMappingProvider xlsFormId={Number(xlsFormId)}>
      <Tabs selected={selectTab()} disableAnimation>
        <Tab label="Details" href="" role="tab" selected tabIndex={0} onClick={() => navigate(`#details`)}>
          {xlsForm && <XlsFormDetails data={xlsForm} mode={mode} changeMode={changeMode} />}
        </Tab>

        {isValid && (
          <Tab label="Field list" href="" role="tab" selected={false} tabIndex={0} onClick={() => navigate(`#list`)}>
            <XlsFormList xlsFormId={Number(xlsFormId)} permissions={permissions} />
          </Tab>
        )}

        {isValid && (
          <Tab
            label="Choice list"
            href=""
            role="tab"
            selected={false}
            tabIndex={0}
            onClick={() => navigate(`#choice-list`)}
          >
            <XlsChoiceList xlsFormId={Number(xlsFormId)} permissions={permissions} />
          </Tab>
        )}

        {permissions && isValid && (
          <Tab
            label="Field mapping"
            href=""
            tabIndex={0}
            role="tab"
            selected={false}
            onClick={() => navigate(`#mapping`)}
          >
            <XslFormMapping formFields={formFields} xlsFormId={Number(xlsFormId)} />
          </Tab>
        )}

        {permissions && isValid && (
          <Tab
            label="Choice list mapping"
            href=""
            tabIndex={0}
            role="tab"
            selected={false}
            onClick={() => navigate(`#choices`)}
          >
            <XlsFormChoice formFields={formFields} />
          </Tab>
        )}

        <Tab label="Export" href="" role="tab" selected={false} tabIndex={0} onClick={() => navigate(`#export`)}>
          {xlsForm && <XlsFormData data={xlsForm} />}
        </Tab>

        {isValid && (
          <Tab
            label="Linked surveys"
            href=""
            role="tab"
            selected={false}
            tabIndex={0}
            onClick={() => navigate(`#linked-surveys`)}
          >
            <XlsSurveyLinked xlsFormId={Number(xlsFormId)} />
          </Tab>
        )}
      </Tabs>
    </XslMappingProvider>
  )
}

export default XlsFormPageHH
